import React, { useState } from 'react';
import _ from 'lodash';

import PropTypes from 'prop-types';
import { Button, Icon, Select } from '@telus-uds/components-web';
import Document from '@telus-uds/palette-allium/build/web/icons/Document';

import ValidateLoader from '../UI/validateLoader';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  quoteStatus,
  vpbRateOtherEnglish,
  vpbRateOtherFrench
} from '../utils/constant';

import request, { apiHost } from '../utils/request';
import { getFormattedDate } from '../utils/utils';
import { getPathVariables } from '../misc';

const Rows = ({ order, removeData, headers, pageLayout }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    quoteApprovalDashboard: {
      selectApprovedPriceError,
      selectPriceLabelDefault,
      approveButtonDefault,
      declineButtonDefault,
      validateMessage,
      vbpPricesLabel
    }
  } = localizedStrings;

  const locale = getPathVariables(window.location.pathname)[0];
  const [approvedPrice, setApprovedPrice] = useState();
  const [actionCalled, setActionCalled] = useState(false);
  const [priceError, setPriceError] = useState(false);

  /**
   * Function to convert price list
   * into json required as options
   * for the Select component
   */
  const convertToOptionsObj = priceArr => {
    const optionObj = [];
    if (priceArr) {
      priceArr.forEach(price => {
        optionObj.push({ text: `$${price}`, value: `${price}` });
      });
    }
    return optionObj;
  };

  /**
   * Function to handle Approve action
   */
  const handleQuoteApprove = () => {
    if (!approvedPrice) {
      setPriceError(true);
      return;
    }
    setPriceError(false);
    setActionCalled(true);

    const updatedPayload = [vpbRateOtherEnglish, vpbRateOtherFrench].includes(
      order.vpbOfficeSeatRate
    )
      ? {
          ...order,
          vpbOfficeSeatRate: approvedPrice,
          quoteStatus: quoteStatus.approved,
          step: 1
        }
      : {
          ...order,
          vpbOfficeSeatRate: approvedPrice,
          quoteStatus: quoteStatus.approved,
          step: 1
        };
    // Custom pricing approval must not modify coupons. Drop the (possibly stale)
    // coupon collections from the payload so the backend preserves the coupons
    // already persisted on the order (prevents the bill credit coupon from being
    // removed on approval).
    delete updatedPayload.appliedCoupons;
    delete updatedPayload.availableDiscountsAndPromotions;

    request
      .put(`/orders/${order.id}/quote`, updatedPayload)
      .then(() => {
        removeData(order.id);
      })
      .catch(() => {
        setActionCalled(false);
      });
  };

  /**
   * Function to handle Decline action
   */
  const handleQuoteDecline = () => {
    setActionCalled(true);

    const updatedPayload = { ...order, quoteStatus: quoteStatus.incomplete };
    // Declining custom pricing must not modify coupons; let the backend keep the
    // persisted set.
    delete updatedPayload.appliedCoupons;
    delete updatedPayload.availableDiscountsAndPromotions;
    request
      .put(`/orders/${order.id}/quote`, updatedPayload)
      .then(() => {
        removeData(order.id);
      })
      .catch(() => {
        setActionCalled(false);
      });
  };

  const getLegacyValue = prefix => {
    const legacyKey = _.find(Object.keys(order), key =>
      key.startsWith(prefix) && _.get(order, key, '') !== ''
    );
    return legacyKey ? _.get(order, legacyKey, '') : '';
  };

  const officeSeatRate = _.get(order, 'vpbOfficeSeatRate', '') || getLegacyValue('vpbOfficeSeatRate_');
  const desiredPrice =
    _.get(order, 'vbpDesiredPrice', '') ||
    getLegacyValue('vbpDesiredPrice_') ||
    _.get(order, 'vbpCompetitionPricing', '');

  const getOptions = () => {
    let result = pageLayout.prices[order.businessConnectEdition];

    if (['Voice', 'Voix'].includes(order.businessConnectEdition)) {
      result = ['Month-to-Month', 'sur une base mensuelle'].includes(
        order.contractTerm
      )
        ? result.MonthToMonth
        : result.Other;
    }

    return convertToOptionsObj(result);
  };

  return (
    <tr>
      <td>
        <a
          href={`${apiHost}/orders/${_.get(
            order,
            'id',
            ''
          )}/quote-pdf?lang=${locale}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon icon={Document} accessibilityLabel="Quote PDF" />
        </a>
      </td>
      <td data-header-label={headers[0]}>{_.get(order, 'orderId', '')}</td>
      <td data-header-label={headers[0]}>{_.get(order, 'customerSegment', '')}</td>
      <td data-header-label={headers[1]}>{`${_.get(
        order,
        'first_name',
        ''
      )} ${_.get(order, 'last_name', '')}`}</td>
      <td data-header-label={headers[2]}>
        {_.get(order, 'typeOfInstallation', '')}
      </td>
      <td data-header-label={headers[3]}>
        {_.get(order, 'businessLegalName', '')}
      </td>
      <td data-header-label={headers[4]}>
        {parseInt(_.get(order, 'starterSeat', 0), 10) +
          parseInt(_.get(order, 'officeSeats', 0), 10)}
      </td>
      <td data-header-label={headers[5]}>
        $
        {[vpbRateOtherEnglish, vpbRateOtherFrench].includes(
          officeSeatRate
        )
          ? desiredPrice
          : officeSeatRate}
      </td>
      <td data-header-label={headers[6]}>
        {order.businessConnectEdition && (
          <Select
            label={`${order.businessConnectEdition} ${vbpPricesLabel}`}
            placeholder={_.get(
              pageLayout,
              'selectPriceLabel',
              selectPriceLabelDefault
            )}
            value={approvedPrice || ''}
            onChange={(value) => {
              setPriceError(false);
              setApprovedPrice(value);
            }}
            validation={priceError ? 'error' : undefined}
            feedback={priceError ? selectApprovedPriceError : undefined}
          >
            {getOptions() && getOptions().map(option => (
              <Select.Item key={option.value} value={option.value}>
                {option.text}
              </Select.Item>
            ))}
          </Select>
        )}
      </td>
      <td data-header-label={headers[7]}>
        {_.get(order, 'vbpNumberFreePromotions', '')}
      </td>
      <td data-header-label={headers[8]}>
        {getFormattedDate(
          _.get(order, 'updatedAt', '2020-07-25T21:03:27.000Z'),
          'YYYY-MM-DD, h:mm a'
        )}
      </td>
      <td data-header-label={headers[9]}>
        {actionCalled ? (
          <ValidateLoader message={validateMessage} />
        ) : (
          <div className="responsive-table__actions">
            <Button variant={{ priority: 'high' }} onPress={handleQuoteApprove}>
              {_.get(pageLayout, 'actionApproveLabel', approveButtonDefault)}
            </Button>
            <Button variant={{ priority: 'low' }} onPress={handleQuoteDecline}>
              {_.get(pageLayout, 'actionDeclineLabel', declineButtonDefault)}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

Rows.propTypes = {
  order: PropTypes.objectOf(PropTypes.object).isRequired,
  removeData: PropTypes.func.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  pageLayout: PropTypes.objectOf(PropTypes.object).isRequired
};

export default Rows;
